import useScrollReveal from "@/hooks/useScrollReveal";

export default function AboutSection() {
  const imgReveal = useScrollReveal({ threshold: 0.1 });
  const statsReveal = useScrollReveal({ threshold: 0.2 });
  const textReveal = useScrollReveal({ threshold: 0.15 });

  return (
    <section className="w-full bg-background-50">

      {/* ============================================================
          DESKTOP / TABLET – Split-screen: kolaż po lewej, tekst po prawej
      ============================================================ */}
      <div className="hidden md:flex w-full items-stretch">

        {/* Lewa kolumna – kolaż zdjęć */}
        <div className="w-full md:w-[52%] xl:w-[50%] relative overflow-hidden animate-fade-in self-center">
          <div className="relative w-full aspect-[16/9] xl:aspect-[2/1]">
            <img
              src="https://storage.readdy-site.link/project_files/4e257fdf-86c3-45fa-9fad-66a82fd8796b/fb0cee85-2010-4710-af2f-58bfe8216ec1_compressed_ChatGPT-Image-25-lip-2026-20_26_47.webp"
              alt="Jan Wojdyła w pracy – analiza finansowania Sanok"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            {/* Gradient blend na styk z tekstem */}
            <div className="absolute inset-y-0 right-0 w-20 xl:w-28 bg-gradient-to-l from-background-50 via-background-50/90 to-transparent" />
            {/* Shimmer */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ mixBlendMode: "overlay" }}>
              <div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-white/6 to-transparent -skew-x-12" />
            </div>
          </div>
        </div>

        {/* Prawa kolumna – treść */}
        <div className="w-full md:w-[48%] xl:w-[50%] flex flex-col justify-center px-6 md:px-10 lg:px-12 xl:px-20 py-12 md:py-14 xl:py-16">
          <div className="max-w-lg xl:max-w-xl">

            <span className="text-[11px] font-semibold tracking-[0.25em] uppercase text-primary-500 mb-4 block animate-fade-in-up">
              O mnie
            </span>

            <h2 className="font-heading text-[2.4rem] xl:text-[2.8rem] font-bold leading-[1.15] text-foreground-950 mb-3 animate-fade-in-up animation-delay-100">
              Poznaj swojego eksperta kredytowego
            </h2>

            <p className="text-sm font-medium tracking-[0.2em] uppercase text-foreground-500 mb-6 animate-fade-in-up animation-delay-200">
              Jan Wojdyła – broker finansowy i ekspert kredytowy
            </p>

            <div className="flex items-center mb-8 animate-fade-in-up animation-delay-300">
              <div className="h-[2px] w-14 bg-primary-500" />
              <div className="w-2 h-2 bg-primary-500 rotate-45 -ml-[4px]" />
              <div className="h-[2px] flex-1 bg-primary-500/15 max-w-[80px]" />
            </div>

            <div className="space-y-4 mb-8 animate-fade-in-up animation-delay-400">
              <p className="text-[15px] text-foreground-600 leading-[1.75]">
                Moja kariera w finansach rozpoczęła się w 1999 roku w Podkarpackim Banku Spółdzielczym. Przez blisko 20 lat przeszedłem ścieżkę od referenta po stanowisko dyrektora departamentu, zdobywając głęboką wiedzę o bankowości, produktach kredytowych, analizie ryzyka i zarządzaniu wierzytelnościami.
              </p>
              <p className="text-[15px] text-foreground-600 leading-[1.75]">
                Obecnie prowadzę samodzielną działalność doradczą pod marką EXPERT KREDYTOWY. W swojej pracy koncentruję się na dokładnym poznaniu sytuacji klienta, jego planów oraz realnych możliwości finansowych.
              </p>
              <p className="text-[15px] text-foreground-600 leading-[1.75]">
                Stawiam na jasną komunikację, rzetelną analizę oraz relacje oparte na zaufaniu. Dzięki doświadczeniu po obu stronach – zarówno jako pracownik banku, jak i niezależny ekspert – doskonale rozumiem, jak efektywnie przeprowadzić proces kredytowy.
              </p>
            </div>

            <div className="flex gap-8 mb-8 animate-fade-in-up animation-delay-500">
              <div className="flex flex-col">
                <span className="text-3xl xl:text-4xl font-heading font-bold text-primary-500">25+</span>
                <span className="text-xs text-foreground-500 mt-1">lat doświadczenia</span>
              </div>
              <div className="w-[1px] bg-background-300" />
              <div className="flex flex-col">
                <span className="text-3xl xl:text-4xl font-heading font-bold text-primary-500">20+</span>
                <span className="text-xs text-foreground-500 mt-1">banków i instytucji</span>
              </div>
              <div className="w-[1px] bg-background-300" />
              <div className="flex flex-col">
                <span className="text-3xl xl:text-4xl font-heading font-bold text-primary-500">5000</span>
                <span className="text-xs text-foreground-500 mt-1">zadowolonych klientów</span>
              </div>
            </div>

            <a
              href="tel:698084044"
              className="inline-flex items-center gap-2 px-7 py-3.5 border-2 border-primary-500 text-primary-500 font-semibold rounded-full hover:bg-primary-50 hover:scale-105 active:scale-95 transition-all duration-300 whitespace-nowrap cursor-pointer animate-fade-in-up animation-delay-600"
            >
              Zadzwoń i umów spotkanie
              <i className="ri-phone-line" />
            </a>
          </div>
        </div>
      </div>

      {/* ============================================================
          MOBILE (< md) – Kolaż na górze + ciemny pasek z tekstem
      ============================================================ */}
      <div className="md:hidden flex flex-col">

        <div
          ref={imgReveal.ref}
          className={`px-4 sm:px-6 pt-6 pb-2 transition-all duration-700 ease-out ${
            imgReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="max-w-sm mx-auto overflow-hidden rounded-xl shadow-lg">
            <img
              src="https://storage.readdy-site.link/project_files/4e257fdf-86c3-45fa-9fad-66a82fd8796b/c0468ca9-7bc7-4af0-8010-9217205d7c90_compressed_ChatGPT-Image-25-lip-2026-21_30_11.webp"
              alt="Jan Wojdyła w pracy – analiza finansowania Sanok"
              className="w-full h-auto object-contain"
            />
          </div>
        </div>

        <div className="bg-foreground-950 px-6 sm:px-8 py-10 sm:py-12">

          <span className="text-[11px] font-semibold tracking-[0.25em] uppercase text-primary-400 mb-3 block animate-fade-in-up">
            O mnie
          </span>

          <p className="font-heading text-2xl sm:text-3xl font-bold text-white mb-2 animate-fade-in-up animation-delay-100">
            Poznaj swojego eksperta kredytowego
          </p>

          <p className="text-xs font-medium tracking-[0.2em] uppercase text-white/50 mb-6 animate-fade-in-up animation-delay-200">
            Jan Wojdyła – broker finansowy i ekspert kredytowy
          </p>

          <div className="space-y-3 mb-8 animate-fade-in-up animation-delay-300">
            <p className="text-[15px] text-white/70 leading-[1.7]">
              Moja kariera w finansach rozpoczęła się w 1999 roku. Przez blisko 20 lat przeszedłem ścieżkę od referenta po dyrektora departamentu w banku. Obecnie prowadzę samodzielną działalność doradczą pod marką EXPERT KREDYTOWY.
            </p>
            <p className="text-[15px] text-white/70 leading-[1.7]">
              Stawiam na jasną komunikację, rzetelną analizę oraz relacje oparte na zaufaniu. Pomagam przejść przez proces kredytowy w sposób spokojny i zrozumiały.
            </p>
          </div>

          <div
            ref={statsReveal.ref}
            className={`flex gap-5 mb-8 transition-all duration-700 ease-out ${
              statsReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <div className="flex flex-col">
              <span className="text-2xl font-heading font-bold text-primary-400">25+</span>
              <span className="text-[11px] text-white/50 mt-1">lat doświadczenia</span>
            </div>
            <div className="w-[1px] bg-white/15" />
            <div className="flex flex-col">
              <span className="text-2xl font-heading font-bold text-primary-400">20+</span>
              <span className="text-[11px] text-white/50 mt-1">banków</span>
            </div>
            <div className="w-[1px] bg-white/15" />
            <div className="flex flex-col">
              <span className="text-2xl font-heading font-bold text-primary-400">5000</span>
              <span className="text-[11px] text-white/50 mt-1">klientów</span>
            </div>
          </div>

          <div
            ref={textReveal.ref}
            className={`transition-all duration-700 ease-out ${
              textReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <a
              href="tel:698084044"
              className="inline-flex items-center gap-2 px-7 py-3.5 border-2 border-primary-500 text-primary-400 font-semibold rounded-full hover:bg-primary-500/10 hover:scale-105 active:scale-95 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Zadzwoń i umów spotkanie
              <i className="ri-phone-line" />
            </a>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-150%) skewX(-12deg); }
          100% { transform: translateX(150%) skewX(-12deg); }
        }
        .animate-shimmer {
          animation: shimmer 5s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
}
